import React from 'react';
import { Clock } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';

const TimingTab = ({ bestTimes }) => {
  if (!bestTimes) return <TabsContent value="timing" />;

  return (
    <TabsContent value="timing" className="space-y-6">
      {/* Recommendation Banner */}
      <Card className="border-emerald-200 bg-emerald-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center flex-shrink-0">
              <Clock className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-emerald-800">Posting Recommendation</h3>
              <p className="text-sm text-emerald-700 mt-1">{bestTimes?.recommendation}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Best Times by Day */}
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Best Times by Day</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {bestTimes?.best_times?.map((day) => (
                <div
                  key={day.day}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    day.recommended ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-slate-700 w-24">{day.day}</span>
                    <Badge
                      variant={day.recommended ? 'default' : 'secondary'}
                      className={day.recommended ? 'bg-emerald-500' : ''}
                    >
                      {day.best_time}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${day.recommended ? 'bg-emerald-500' : 'bg-slate-400'}`}
                        style={{ width: `${day.engagement_score}%` }}
                      />
                    </div>
                    <span className="text-sm text-slate-500 w-10">{day.engagement_score}%</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Hourly Heatmap */}
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Engagement Heatmap</CardTitle>
            <CardDescription>Higher scores indicate better posting times</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bestTimes?.hourly_heatmap?.filter((_, i) => i % 3 === 0) || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" stroke="#94a3b8" fontSize={10} />
                  <YAxis stroke="#94a3b8" fontSize={11} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                  />
                  <Legend />
                  <Bar dataKey="monday" name="Mon" fill="#10B981" stackId="a" />
                  <Bar dataKey="wednesday" name="Wed" fill="#8B5CF6" stackId="a" />
                  <Bar dataKey="friday" name="Fri" fill="#F59E0B" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </TabsContent>
  );
};

export default TimingTab;
